import {
    formatCurrency,
    formatDate,
    formatNumber,
    formatPercent,
} from '@angular/common';
import { isNumber, round } from 'lodash';
import { DateTime } from 'luxon';
import { DatiqDayOfWeek, DatiqFilterComparison } from './datiq-app.enum';
import {
    IDatiqDateRange,
    IDatiqFilter,
    IDatiqGroupBy,
    IDatiqLink,
    IDatiqRoute,
} from './datiq-app.interface';
import { DATIQ_DEFAULT_STRING } from './datiq-app-default-values';

export const DATIQ_APP = 'Datiq App';
export const DATIQ_APP_NAME = 'DAT iQ';
export const DATIQ_DATE_FORMAT_PIPE = 'MM/dd/yyyy';
export const DATIQ_DATE_FORMAT = 'MM/dd/yyyy';
export const DATIQ_DATE_FORMAT_MONTHLY = 'MMMM yyyy';
export const DATIQ_DATE_FORMAT_ANALYTICS = 'yyyyMMdd';
export const DATIQ_DATE_FORMAT_LOCALE = 'en-US';
export const DATIQ_DEFAULT_ERROR_MESSAGE = 'An error has occurred';
export enum DatiqEnvironments {
    Prod = 'prod',
    Test = 'test',
    Beta = 'beta',
    Unknown = '',
    Staging = "staging"
}
export const DATIQ_UNAV_SIGN_OUT_LABEL = 'Log Out';
export const DATIQ_UNAV_CHAT_LIVE_LABEL = 'Live Support';
export const DATIQ_UNAV_ACCOUNT_INFORMATION_LABEL = 'Account Information';
export const DATIQ_MILLISECONDS = 1000;
export const datiqGetMilliseconds: (seconds: number) => number = (
    seconds: number,
): number => seconds * DATIQ_MILLISECONDS;
export const DATIQ_ENTITY_NAME_MIN_LENGTH = 5;
export const DATIQ_ENTITY_CUSTOMER_NAME_MIN_LENGTH = 3;
export const DATIQ_ENTITY_NAME_MAX_LENGTH = 100;
export const DATIQ_ENTITY_NAME_PATTERN = "[a-zA-Z0-9-_ &@.?!-,' ]*";
export const DATIQ_ENTITY_NAME_PATTERN_AUTOCOMPLETE =
    "^([a-zA-Z0-9-_ &@.?!-,' ]{3,100})$";
export const DATIQ_FILTER_EMPTY: IDatiqFilter = {
    key: '',
    comparison: DatiqFilterComparison.Equals,
    value: null,
};
export const DATIQ_DEFAULT_PAGE_TITLE = "DAT iQ Don't Guess. Know.";

export const formatFullNames: (
    firstName: string,
    lastName: string,
    middle?: string,
) => string = (
    firstName: string,
    lastName: string,
    middle: string = '',
): string => {
    return `${firstName}${!!middle?.length ? ' ' + middle : ''} ${lastName}`;
};

export const datiqConvertHexString = (hexString: string): number => {
    return Number(`0x${hexString.replace(/^#/, '')}`);
};
export const datiqRoundCurrency = (num: number): number => {
    const fixedTrip: string = !isNaN(num) ? num?.toFixed(2) : '';
    const coinParts: string[] =
        !!fixedTrip?.length && fixedTrip.indexOf('.') >= 0
            ? fixedTrip.split('.')
            : [];

    const coinsString: string = !!coinParts[1]?.length ? coinParts[1] : '';
    const tripRoundedString: string = !!coinParts[0] ? coinParts[0] : '';

    const coins = Number(coinsString);
    const tripRounded = Number(tripRoundedString);
    const hasCoinsAndTripsRounded: boolean = isNaN(coins) && isNaN(tripRounded);

    return hasCoinsAndTripsRounded
        ? null
        : coins < 50
        ? tripRounded
        : coins > 50
        ? tripRounded + 1
        : coins === 50
        ? datiqBankersRoundToEven(tripRounded)
        : null;
};
export const datiqBankersRoundToEven = (num: number): number => {
    return num % 2 === 0 ? num : num + 1;
};
export const datiqGetTodaysDate: () => Date = () => {
    const today: Date = new Date();
    today.setHours(0, 0, 0, 0);
    return today;
};
export const datiqGetDateString: (date: Date, format?: string) => string = (
    date: Date,
    format: string = DATIQ_DATE_FORMAT,
): string => formatDate(date, format, 'en-US');
export const datiqGetDateStringMonthDay: (
    date: Date,
    format?: string,
) => string = (date: Date): string => formatDate(date, 'MM/dd', 'en-US');
export const datiqGetDateStringMonthDayFull: (
    date: Date,
    format?: string,
) => string = (date: Date): string => formatDate(date, 'MMMM dd', 'en-US');
export const datiqGetDateStringMonthYearShort: (
    date: Date,
    format?: string
) => string = (date: Date): string => formatDate(date, `MMM ''yy`, 'en-US');
export const datiqGetDateStringMonthYearFull: (
    date: Date,
    format?: string,
) => string = (date: Date): string => formatDate(date, 'MMMM yyyy', 'en-US');
export const datiqGetDateStringDaily: (
    date: Date,
    format?: string,
) => string = (date: Date): string =>
    formatDate(date, 'MMMM dd, yyyy', 'en-US');
export const datiqGetDateStringWeekly: (
    date: Date,
    goForward?: boolean,
    format?: string,
) => string = (date: Date, goForward: boolean, format: string): string => {
    const dateObj = DateTime.fromJSDate(date);
    const startOfWeek = !goForward
        ? dateObj.minus({ week: 1 }).endOf('week').startOf('day')
        : dateObj;
    const endOfWeek = !goForward
        ? dateObj.endOf('week').minus({ day: 1 }).startOf('day')
        : dateObj.plus({ week: 1 }).minus({ day: 1 }).startOf('day');
    const startOfWeekMonth = startOfWeek.toFormat('MMMM');
    const startOfWeekDays = startOfWeek.toFormat('dd');
    const startOfWeekYearsString = startOfWeek.toFormat('yyyy');
    const endOfWeekMonthString = endOfWeek.toFormat('MMMM');
    const endOfWeekDays = endOfWeek.toFormat('dd');
    const endOfWeekYears = endOfWeek.toFormat('yyyy');
    const endOfWeekMonth =
        endOfWeekMonthString === startOfWeekMonth
            ? ''
            : `${endOfWeekMonthString} `;
    const startOfWeekYears =
        startOfWeekYearsString === endOfWeekYears
            ? ''
            : `, ${startOfWeekYearsString} `;

    return `${startOfWeekMonth} ${startOfWeekDays}${startOfWeekYears} - ${endOfWeekMonth}${endOfWeekDays}, ${endOfWeekYears}`;
};
export const datiqGetDateStringMonthly: (
    date: Date,
    format?: string,
) => string = (date: Date): string => datiqGetDateStringMonthYearFull(date);
export const datiqGetDateStringYearly: (
    date: Date,
    format?: string,
) => string = (date: Date): string => formatDate(date, 'yyyy', 'en-US');
export const datiqGetCurrencyString: (num: number) => string = (
    num: number,
): string => formatCurrency(datiqRoundCurrency(num), 'en-US', '$');
export const datiqGetPercentString = (num: number): string => {
    return `${round(num, 1)}%`;
};
export const datiqGetCurrencyStringWithoutCents: (num: number) => string = (
    num: number,
): string =>
    formatCurrency(datiqRoundCurrency(num), 'en-US', '$').split('.')[0];
export const datiqGetCurrencyStringNoRounding: (num: number) => string = (
    num: number,
): string => formatCurrency(num, 'en-US', '$', 'USD', '1.2-2');
export const DATIQ_PAGE_TITLE: (page: string) => string = (page: string) =>
    `${page} - DAT iQ`;
export const datiqRangeStringCurrency = (low: number, high: number): string => {
    return `(${datiqGetCurrencyStringWithoutCents(
        low,
    )} - ${datiqGetCurrencyStringWithoutCents(high)})`;
};
export const datiqRangeStringCurrencyTripOrMile = (
    mileage: number,
    perTrip: boolean,
    low: number,
    high: number,
): string => {
    return perTrip
        ? `(${datiqGetCurrencyStringWithoutCents(
              low,
          )} - ${datiqGetCurrencyStringWithoutCents(high)})`
        : `(${datiqGetCurrencyStringNoRounding(
              low / mileage,
          )} - ${datiqGetCurrencyStringNoRounding(high / mileage)})`;
};
export const datiqDateRangeString = (range: IDatiqDateRange): string =>
    `${datiqGetDateString(range.start)} - ${datiqGetDateString(range.end)}`;
export const emptyDatiqLink: IDatiqLink = {};
export const calcPercentChange = (
    newNumber: number,
    originalNumber: number,
): number => {
    const increase: number = newNumber - originalNumber;

    return round(increase / originalNumber, 3);
};

export const datiqGetCleanedNumber = (number: number): number => {
    return isNumber(number) ? number : 0;
};
export const datiqFormatPercent = (percentNum: number): string => {
    const cleanedPercentNumber: number = datiqGetCleanedNumber(percentNum);

    return formatPercent(cleanedPercentNumber, 'en-US', '.1');
};

export const datiqFormatNumber = (num: number): string => {
    return formatNumber(num, 'en-US');
};

export const datiqGetXWeekTimeframe = (
    weekAmount: number,
    timeframeEnd = new Date(),
): IDatiqDateRange => {
    const xWeeks = weekAmount * 7 * 24 * 60 * 60 * 1000;
    const start = new Date(timeframeEnd.getTime() - xWeeks);
    return {
        start,
        end: timeframeEnd,
    };
};

export const datiqIsDayOfWeek = (
    date: Date,
    dayOfWeek: DatiqDayOfWeek,
): boolean => {
    return date.getDay() === dayOfWeek;
};

export const datiqGetClosestNextDayOfWeekDate = (
    date: Date,
    dayOfWeek: DatiqDayOfWeek,
): Date => {
    const currentDayOfWeek =
        date.getDay() === DatiqDayOfWeek.Sunday ? 7 : date.getDay();
    const newDate = date.getUTCDate() + (7 - currentDayOfWeek + dayOfWeek);

    return new Date(date.setDate(newDate));
};

export const datiqGetClosestPreviousDayOfWeekDate = (
    date: Date,
    dayOfWeek: DatiqDayOfWeek,
): Date => {
    const newDate = date.getUTCDate() - date.getDay() + dayOfWeek;

    return new Date(date.setDate(newDate));
};

export const datiqGetClosestDateByDay = (
    date: Date,
    dayOfWeek: DatiqDayOfWeek,
    past: boolean,
): Date => {
    return past
        ? datiqGetClosestPreviousDayOfWeekDate(date, dayOfWeek)
        : datiqGetClosestNextDayOfWeekDate(date, dayOfWeek);
};

export const datiqRoundFloat = (num: number): number => round(num, 2);

export const emptyDatiqGroupBy: IDatiqGroupBy = {
    dayOfWeek: DatiqDayOfWeek.Monday,
    count: 4,
    past: true,
};

export const DATIQ_CI_SPERATOR = '_';
export const emptyDatiqRoute: IDatiqRoute = {
    path: DATIQ_DEFAULT_STRING,
};